import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAcademicStructure,
  createAcademicGroup,
  updateAcademicGroup,
  deleteAcademicGroup,
  moveAcademicGroup,
  fetchSubjectCatalog,
  fetchOfferedSubjects,
  offerSubjects as offerSubjectsApi,
  removeOfferedSubject as removeOfferedSubjectApi,
  attachSubjectToGroup,
  detachSubjectFromGroup,
  setSubjectScheduling as setSubjectSchedulingApi,
  fetchElectiveRoster,
  enrollElective as enrollElectiveApi,
  unenrollElective as unenrollElectiveApi,
  applyAcademicStructureTemplate,
} from "../service/academicsService";
import { errorMessage } from "@/lib/notify";
import type { AcademicsState, CreateGroupPayload, AttachSubjectPayload, SetSubjectSchedulingPayload } from "../types";
import i18n from "@/i18n";

export function electiveRosterKey(groupId: number, subjectId: number, academicYearId: number) {
  return `${groupId}-${subjectId}-${academicYearId}`;
}

export const loadAcademicStructure = createAsyncThunk(
  "academics/loadStructure",
  async (institutionId: number) => {
    return await fetchAcademicStructure(institutionId);
  }
);

export const applyTemplateToInstitution = createAsyncThunk(
  "academics/applyTemplate",
  async (
    { institutionId, templateId, force }: { institutionId: number; templateId: number; force?: boolean },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const result = await applyAcademicStructureTemplate(institutionId, templateId, force);
      await dispatch(loadAcademicStructure(institutionId));
      return result;
    } catch (err: any) {
      return rejectWithValue({
        message: err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToApplyTemplate"),
        existingGroupCount: err.response?.data?.existingGroupCount as number | undefined,
      });
    }
  }
);

export const addAcademicGroup = createAsyncThunk(
  "academics/addGroup",
  async (
    { institutionId, payload }: { institutionId: number; payload: CreateGroupPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await createAcademicGroup(institutionId, payload);
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("academics:academicsSlicetsMsgs.failedToCreateAcademicGroup")));
    }
    // Refetch the whole tree — safest way to reflect nested parent/child changes
    await dispatch(loadAcademicStructure(institutionId));
  }
);

export const moveGroup = createAsyncThunk(
  "academics/moveGroup",
  async (
    { institutionId, groupId, direction }: { institutionId: number; groupId: number; direction: "UP" | "DOWN" },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await moveAcademicGroup(institutionId, groupId, direction);
      await dispatch(loadAcademicStructure(institutionId));
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("academics:academicsSlicetsMsgs.failedToMoveAcademicGroup")));
    }
  }
);

export const renameAcademicGroup = createAsyncThunk(
  "academics/renameGroup",
  async (
    { institutionId, groupId, name }: { institutionId: number; groupId: number; name: string },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await updateAcademicGroup(institutionId, groupId, { name });
      await dispatch(loadAcademicStructure(institutionId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToRenameAcademicGroup"));
    }
  }
);

// Refused server-side (409) if the node still has children, or has ever
// had a student/elective enrolled under it — the rejection message from
// the backend explains exactly which of those blocked it, surfaced here
// via rejectWithValue instead of the generic "Rejected" thunk message.
export const removeAcademicGroup = createAsyncThunk(
  "academics/removeGroup",
  async (
    { institutionId, groupId }: { institutionId: number; groupId: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await deleteAcademicGroup(institutionId, groupId);
      await dispatch(loadAcademicStructure(institutionId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToDeleteAcademicGroup"));
    }
  }
);

/**
 * Adds several sibling nodes in one action — e.g. checking "Section A" and
 * "Section B" (but not C/D) under one Class in the Add Group picker. Each
 * item carries its own groupType since the applied template's matched
 * children can (rarely) mix types at one level. Runs the creates
 * sequentially (each is a separate row, no bulk endpoint) then refetches
 * the tree once at the end, not once per node.
 */
export const addAcademicGroups = createAsyncThunk(
  "academics/addGroups",
  async (
    {
      institutionId,
      parentId,
      items,
    }: { institutionId: number; parentId?: number; items: { name: string; groupType: string; displayOrder?: number }[] },
    { dispatch, rejectWithValue }
  ) => {
    // Keeps going past a failed item so one clash doesn't leave the rest unattempted; reports exactly what was and wasn't added.
    const failures: string[] = [];
    for (const item of items) {
      try {
        await createAcademicGroup(institutionId, {
          name: item.name,
          groupType: item.groupType,
          // The template's own position keeps classes in order (promotion relies on it); with none, the server appends.
          ...(item.displayOrder ? { displayOrder: item.displayOrder } : {}),
          ...(parentId ? { parentId } : {}),
        });
      } catch (err) {
        failures.push(`${item.name}: ${errorMessage(err, "failed")}`);
      }
    }
    await dispatch(loadAcademicStructure(institutionId));
    if (failures.length) {
      const added = items.length - failures.length;
      return rejectWithValue(`${added} of ${items.length} added. Not added — ${failures.join("; ")}`);
    }
  }
);

export const loadSubjectCatalog = createAsyncThunk("academics/loadSubjects", async () => {
  return await fetchSubjectCatalog();
});

export const loadOfferedSubjects = createAsyncThunk(
  "academics/loadOfferedSubjects",
  async (institutionId: number) => {
    return await fetchOfferedSubjects(institutionId);
  }
);

export const offerSubjects = createAsyncThunk(
  "academics/offerSubjects",
  async (
    { institutionId, subjectIds }: { institutionId: number; subjectIds: number[] },
    { rejectWithValue }
  ) => {
    try {
      return await offerSubjectsApi(institutionId, subjectIds);
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToAddOfferedSubjects"));
    }
  }
);

export const removeOfferedSubject = createAsyncThunk(
  "academics/removeOfferedSubject",
  async (
    { institutionId, subjectId }: { institutionId: number; subjectId: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await removeOfferedSubjectApi(institutionId, subjectId);
      await dispatch(loadOfferedSubjects(institutionId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToRemoveOfferedSubject"));
    }
  }
);

export const attachSubject = createAsyncThunk(
  "academics/attachSubject",
  async (
    { institutionId, payload }: { institutionId: number; payload: AttachSubjectPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await attachSubjectToGroup(institutionId, payload);
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("academics:academicsSlicetsMsgs.failedToAttachSubject")));
    }
    await dispatch(loadAcademicStructure(institutionId));
  }
);

export const detachSubject = createAsyncThunk(
  "academics/detachSubject",
  async (
    { institutionId, groupId, subjectId }: { institutionId: number; groupId: number; subjectId: number },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await detachSubjectFromGroup(institutionId, groupId, subjectId);
    } catch (err) {
      return rejectWithValue(errorMessage(err, i18n.t("academics:academicsSlicetsMsgs.failedToDetachSubject")));
    }
    await dispatch(loadAcademicStructure(institutionId));
  }
);

export const updateSubjectScheduling = createAsyncThunk(
  "academics/setSubjectScheduling",
  async (
    { institutionId, payload }: { institutionId: number; payload: SetSubjectSchedulingPayload },
    { dispatch, rejectWithValue }
  ) => {
    try {
      await setSubjectSchedulingApi(institutionId, payload);
      await dispatch(loadAcademicStructure(institutionId));
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("academics:academicsSlicetsMsgs.failedToSaveScheduling"));
    }
  }
);

export const loadElectiveRoster = createAsyncThunk(
  "academics/loadElectiveRoster",
  async ({
    institutionId,
    groupId,
    subjectId,
    academicYearId,
  }: {
    institutionId: number;
    groupId: number;
    subjectId: number;
    academicYearId: number;
  }) => {
    const roster = await fetchElectiveRoster(institutionId, groupId, subjectId, academicYearId);
    return { key: electiveRosterKey(groupId, subjectId, academicYearId), roster };
  }
);

export const enrollElective = createAsyncThunk(
  "academics/enrollElective",
  async (
    {
      institutionId,
      groupId,
      subjectId,
      academicYearId,
      studentId,
    }: { institutionId: number; groupId: number; subjectId: number; academicYearId: number; studentId: number },
    { dispatch }
  ) => {
    await enrollElectiveApi(institutionId, groupId, subjectId, studentId);
    await dispatch(loadElectiveRoster({ institutionId, groupId, subjectId, academicYearId }));
  }
);

export const unenrollElective = createAsyncThunk(
  "academics/unenrollElective",
  async (
    {
      institutionId,
      groupId,
      subjectId,
      academicYearId,
      enrollmentId,
    }: { institutionId: number; groupId: number; subjectId: number; academicYearId: number; enrollmentId: number },
    { dispatch }
  ) => {
    await unenrollElectiveApi(institutionId, groupId, subjectId, enrollmentId);
    await dispatch(loadElectiveRoster({ institutionId, groupId, subjectId, academicYearId }));
  }
);

const initialState: AcademicsState = {
  groups: [],
  subjects: [],
  offeredSubjects: [],
  electiveRosters: {},
  loading: false,
  error: null,
};

const academicsSlice = createSlice({
  name: "academics",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadAcademicStructure.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadAcademicStructure.fulfilled, (state, action) => {
        state.loading = false;
        state.groups = action.payload;
      })
      .addCase(loadAcademicStructure.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToLoadAcademicStructure");
      })
      .addCase(addAcademicGroup.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToCreateAcademicGroup");
      })
      .addCase(addAcademicGroups.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToAddOneOr");
      })
      .addCase(applyTemplateToInstitution.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToApplyTemplate");
      })
      .addCase(renameAcademicGroup.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToRenameAcademicGroup");
      })
      .addCase(removeAcademicGroup.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToDeleteAcademicGroup");
      })
      .addCase(loadSubjectCatalog.fulfilled, (state, action) => {
        state.subjects = action.payload;
      })
      .addCase(loadOfferedSubjects.fulfilled, (state, action) => {
        state.offeredSubjects = action.payload;
      })
      .addCase(loadOfferedSubjects.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToLoadOfferedSubjects");
      })
      .addCase(offerSubjects.fulfilled, (state, action) => {
        state.offeredSubjects = action.payload;
      })
      .addCase(offerSubjects.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToAddOfferedSubjects");
      })
      .addCase(removeOfferedSubject.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToRemoveOfferedSubject");
      })
      .addCase(attachSubject.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("academics:academicsSlicetsMsgs.failedToAttachSubject");
      })
      .addCase(loadElectiveRoster.fulfilled, (state, action) => {
        state.electiveRosters[action.payload.key] = action.payload.roster;
      })
      .addCase(loadElectiveRoster.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToLoadElectiveRoster");
      })
      .addCase(enrollElective.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToEnrollStudentIn");
      })
      .addCase(unenrollElective.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("academics:academicsSlicetsMsgs.failedToRemoveStudentFrom");
      });
  },
});

export default academicsSlice.reducer;